import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { WorkRequestData, WorkRequestLine, WorkLineSource } from "./types";
import { getWorkRequestById, updateWorkRequest, deleteWorkRequest } from "./mockData/workRequestData";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";

const { Panel } = AntCollapse;

const WorkRequestView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workRequest, setWorkRequest] = useState<WorkRequestData | null>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const [currentUserId] = useState(1); // Mock current user ID
  const [approverIds] = useState([1, 2, 3]); // Mock approver IDs

  useEffect(() => {
    if (id) {
      loadWorkRequestData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkRequestData = async (workRequestId: string) => {
    try {
      setLoading(true);
      const data:any = getWorkRequestById(workRequestId);
      
      if (data) {
        setWorkRequest(data);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Work Request not found",
        });
      }
    } catch (error) {
      console.error("Error loading work request:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load work request data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (workRequest) {
      navigate(`/work-request-creation?edit=${workRequest.id}`);
    }
  };

  const handleDuplicate = () => {
    if (workRequest) {
      navigate(`/work-request-creation?duplicate=${workRequest.id}`);
    }
  };

  const handleDelete = () => {
    if (!workRequest) return;

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const success = deleteWorkRequest(workRequest.id);
        if (success) {
          Swal.fire("Deleted!", "Work Request has been deleted.", "success").then(() => {
            navigate('/work-request-list');
          });
        } else {
          Swal.fire("Error!", "Failed to delete work request.", "error");
        }
      }
    });
  };

  const handleApprove = async () => {
    try {
      setDisableApprove(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const updated:any = updateWorkRequest(workRequest!.id, {
        status: "Approved",
        approverComments: "Approved via Work Request view"
      });
      
      if (updated) {
        setWorkRequest(updated);
        Swal.fire(
          "Approved!",
          "Work Request has been approved successfully",
          "success"
        ).then(() => {
          navigate("/work-request-list");
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve Work Request", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const updated:any = updateWorkRequest(workRequest!.id, {
        status: "Rejected",
        rejectionComments: rejectedReason
      });

      if (updated) {
        setWorkRequest(updated);
        Swal.fire("Rejected!", "Work Request has been rejected", "success").then(
          () => {
            navigate("/work-request-list");
          }
        );
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject Work Request", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work Request",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleReject(result.value);
      }
    });
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      "Draft": "secondary",
      "Pending Approval": "warning",
      "Approved": "success",
      "Rejected": "danger",
      "In Progress": "info",
      "Completed": "success",
    };
    return statusColors[status] || "secondary";
  };

  const getSourceColor = (source: WorkLineSource) => {
    const colors = {
      [WorkLineSource.ProjectManagement]: 'blue',
      [WorkLineSource.WorkBOQ]: 'green', 
      [WorkLineSource.NewWorkLine]: 'orange'
    };
    return colors[source] || 'default';
  };

  // Material BOQ expandable row component for view
  const expandedRowRender = (record: WorkRequestLine) => {
    const materialAllocation = (record as any).materialAllocation;
    
    if (!materialAllocation || materialAllocation.length === 0) {
      return null;
    }

    return (
      <div className="expandableBody">
        <div style={{ flex: 1, textAlign: "left" }}>
          <div className="mb-3">
            <strong>📦 Material BOQ Allocation ({materialAllocation.length} items):</strong>
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              {materialAllocation.map((material: any, index: number) => (
                <li key={index} style={{ marginBottom: "5px" }}>
                  <strong>{material.materialName}</strong> - 
                  Qty: {material.quantity} {material.uom}
                  {material.category && (
                    <span style={{ marginLeft: "10px" }}>
                      <Tag color="blue">{material.category}</Tag>
                    </span>
                  )}
                  {material.materialId && (
                    <span style={{ marginLeft: "5px", color: "#666", fontSize: "12px" }}>
                      (ID: {material.materialId})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      fixed: "left",
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div>{text}</div>
          {record.subcategory && (
            <small className="text-muted">{record.subcategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 130,
      render: (quantity: number, record: WorkRequestLine) => {
        const availableQty = (record as any).availableQuantity || quantity;
        const requestedQty = (record as any).requestedQuantity || quantity;
        
        if (availableQty !== requestedQty) {
          return (
            <div>
              <div className="text-muted small">Available: {availableQty.toLocaleString()}</div>
              <div><strong>Requested: {requestedQty.toLocaleString()}</strong> {record.uom}</div>
            </div>
          );
        }
        
        return `${formatDecimal(quantity)} ${record?.uom}`;
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source: string, record: WorkRequestLine) => {
        const materialAllocation = (record as any).materialAllocation;
        const hasMaterialBOQ = materialAllocation && materialAllocation.length > 0;
        
        return (
          <div>
            <Tag color={getSourceColor(source as WorkLineSource)}>{source}</Tag>
            {hasMaterialBOQ && (
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '10px' }}>📦 Material BOQ</Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Location",
      key: "location",
      width: 120,
      render: (_: any, record: WorkRequestLine) => {
        const location = [];
        if (record.assignedTower) location.push(record.assignedTower);
        if (record.assignedVilla) location.push(record.assignedVilla);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Planned Dates",
      key: "plannedDates",
      width: 150,
      render: (_: any, record: WorkRequestLine) => (
        <div>
          {record.plannedStartDate && (
            <div><small>Start: {record.plannedStartDate}</small></div>
          )}
          {record.plannedEndDate && (
            <div><small>End: {record.plannedEndDate}</small></div>
          )}
          {!record.plannedStartDate && !record.plannedEndDate && '-'}
        </div>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) =>
        record.documents?.length > 0 ? (
          <CheckCircleOutlined style={{ color: "#28a745" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#dc3545" }} />
        ),
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      "In Progress": { title: "In Progress", color: "info" },
      Completed: { title: "Completed", color: "success" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Request Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workRequest) {
    return (
      <>
        <Head title="Work Request Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Request not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workRequest.status);

  return (
    <>
      <Head title={`Work Request - ${workRequest.workRequestNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workRequest.workRequestNumber} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons based on status */}
                    {workRequest.status === "Draft" && (
                      <div className="actions clearfix">
                        <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                          <li className="pt-0 pb-0">
                            <Button color="primary" onClick={handleEdit}>
                              <EditOutlined /> Edit
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button color="secondary" onClick={handleDuplicate}>
                              <CopyOutlined /> Duplicate
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button color="danger" onClick={handleDelete}>
                              <DeleteOutlined /> Delete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    {workRequest.status === "Pending Approval" &&
                      allowedActions?.approve === 1 &&
                      approverIds.includes(currentUserId) && (
                        <div className="actions clearfix">
                          <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                            <li className="pt-0 pb-0">
                              {disableApprove ? (
                                <Button color="success" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="success"
                                  type="button"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Are you sure?",
                                      text: "Do you want to approve this Work Request?",
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonText: "Yes, Approve it!",
                                      cancelButtonText: "No, keep it",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        handleApprove();
                                      }
                                    });
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                            </li>
                            <li className="pt-0 pb-0">
                              {disableReject ? (
                                <Button color="danger" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="danger"
                                  onClick={showRejectModal}
                                  type="button"
                                  className="btn-dim btn btn-secondary"
                                >
                                  Reject
                                </Button>
                              )}
                            </li>
                          </ul>
                        </div>
                      )}

                    <div className="actions clearfix">
                      <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate("/work-request-list");
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert
                color={"danger"}
                isOpen={workRequest.status === "Rejected"}
              >
                <strong>Rejected: </strong>
                {(workRequest as any).rejectionComments || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Request #</div>
                          <div className="data-value w-70">
                            {workRequest.workRequestNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workRequest.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workRequest.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Request For</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workRequest.requestFor || "--"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Approval Required</div>
                          <div className="data-value w-70">
                            <Badge color={workRequest.approvalRequired ? "outline-warning" : "outline-success"}>
                              {workRequest.approvalRequired ? "Yes" : "No"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Total Lines</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workRequest.totalLines || 0}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created By</div>
                          <div className="data-value w-70">
                            {workRequest.createdBy || "--"}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Title</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workRequest.workRequestTitle}</p>
                                </div>
                              }
                              title={"Work Request Title"}
                            >
                              {workRequest.workRequestTitle?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workRequest.workRequestTitle.substring(0, 30)}...
                                </span>
                              ) : (
                                workRequest.workRequestTitle || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workRequest.description || "No description provided"}</p>
                                </div>
                              }
                              title={"Description"}
                            >
                              {workRequest.description && workRequest.description.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workRequest.description.substring(0, 30)}...
                                </span>
                              ) : (
                                workRequest.description || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Start Date</div>
                          <div className="data-value w-70">
                            {dayjs(workRequest.plannedStartDate).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">End Date</div>
                          <div className="data-value w-70">
                            {dayjs(workRequest.plannedEndDate).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel header={`Work Request Lines (${workRequest.totalLines})`} key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workRequest.workLines || workRequest.workLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work request lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workLinesColumns}
                            dataSource={workRequest.workLines}
                            scroll={{ x: 1000 }}
                            expandable={{
                              expandedRowRender,
                              expandIcon: ({ expanded, onExpand, record }) => {
                                const materialAllocation = (record as any).materialAllocation;
                                const hasMaterialBOQ = materialAllocation && materialAllocation.length > 0;
                                
                                if (!hasMaterialBOQ) return null;
                                
                                return expanded ? (
                                  <DownOutlined 
                                    onClick={e => onExpand(record, e)} 
                                    style={{ color: '#1890ff', cursor: 'pointer' }}
                                  />
                                ) : (
                                  <RightOutlined 
                                    onClick={e => onExpand(record, e)} 
                                    style={{ color: '#1890ff', cursor: 'pointer' }}
                                  />
                                );
                              },
                              rowExpandable: (record) => {
                                const materialAllocation = (record as any).materialAllocation;
                                return materialAllocation && materialAllocation.length > 0;
                              },
                            }}
                            bordered
                            size="small"
                            pagination={false}
                          />
                        )}
                      </div>

                      {workRequest.workLines?.length > 0 && (
                        <Row style={{ margin: "20px" }}>
                          <Col className="justify-end">
                            <div className="w-40">
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Work Lines</strong>
                                </div>
                                <span className="r_align">
                                  {workRequest.workLines.length}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Quantity</strong>
                                </div>
                                <span className="r_align">
                                  {formatDecimal(
                                    workRequest.workLines.reduce(
                                      (total: number, line: any) =>
                                        total + (Number(line.quantity) || 0),
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Work Categories</strong>
                                </div>
                                <span className="r_align">
                                  {
                                    new Set(
                                      workRequest.workLines.map(
                                        (line: any) => line.category
                                      )
                                    ).size
                                  }
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Panel>

                  {/* Status History Panel */}
                  <Panel header="Status History" key="3">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p><strong>Work Request Created</strong></p>
                        <p className="text-muted">
                          {dayjs(workRequest.createdAt).format("DD MMM YYYY HH:mm")} by {workRequest.createdBy}
                        </p>
                      </Timeline.Item>
                      
                      {workRequest.status === "Pending Approval" && (
                        <Timeline.Item color="orange">
                          <p><strong>Submitted for Approval</strong></p>
                          <p className="text-muted">
                            {dayjs(workRequest.updatedAt || workRequest.createdAt).format("DD MMM YYYY HH:mm")}
                          </p>
                        </Timeline.Item>
                      )}

                      {workRequest.status === "Approved" && (
                        <Timeline.Item color="green">
                          <p><strong>Approved</strong></p>
                          <p className="text-muted">
                            {dayjs(workRequest.updatedAt || workRequest.createdAt).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workRequest as any).approverComments && (
                            <p className="text-muted">Comments: {(workRequest as any).approverComments}</p>
                          )}
                        </Timeline.Item>
                      )}

                      {workRequest.status === "Rejected" && (
                        <Timeline.Item color="red">
                          <p><strong>Rejected</strong></p>
                          <p className="text-muted">
                            {dayjs(workRequest.updatedAt || workRequest.createdAt).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workRequest as any).rejectionComments && (
                            <p className="text-muted">Reason: {(workRequest as any).rejectionComments}</p>
                          )}
                        </Timeline.Item>
                      )}
                    </Timeline>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkRequestView;